import React, { useContext } from "react";
import {
  getAddRoute,
  getCloneLink,
  getCloneRoute,
  getDetailLink,
  getDetailRoute,
  getEditLink,
  getEditRoute,
  getIndexRoute,
  ResourceName,
  RouteKey,
} from "../../Routing";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { GenericEntityConstantsEntities, getEntityConstants } from "../../../api/GenericConstants";
import { useGet } from "../../../api/BaseEntityApi";
import { LoadingWrapper } from "../../../common/LoadingWrapper";

interface LegacyRouteRedirectProps {
  from: RouteKey;
  to: RouteKey;
  index?: boolean;
  detail?: boolean;
  create?: boolean;
  edit?: boolean;
  clone?: boolean;
  children?: React.ReactNode;
}

/**
 * Component that handles legacy route redirections.
 * @author @CorradoSurmanowicz
 * @param {Object} props - The properties object.
 * @param {string} props.from - The source route to redirect from.
 * @param {string} props.to - The destination route to redirect to.
 * @param {boolean} [props.index=true] - Whether to redirect the table route.
 * @param {boolean} [props.detail=true] - Whether to redirect the detail route.
 * @param {boolean} [props.create=true] - Whether to redirect the create route.
 * @param {boolean} [props.edit=true] - Whether to redirect the edit route.
 * @param {boolean} [props.clone=true] - Whether to redirect the clone route.
 * @param {React.ReactNode} [props.children] - Additional child components to render.
 * @returns {JSX.Element} The rendered component.
 */
export const LegacyRouteRedirect = ({
  from,
  to,
  index = true,
  detail = true,
  create = true,
  edit = true,
  clone = true,
  children,
}: LegacyRouteRedirectProps) => {
  const { route } = useContext(SessionContext);

  return (
    <RouteRedirect
      from={from}
      index={index ? (props) => <Redirect to={route(getIndexRoute(to))} /> : undefined}
      detail={detail ? (props) => <Redirect to={route(getDetailLink(to, props.match.params.id ?? ""))} /> : undefined}
      create={create ? (props) => <Redirect to={route(getAddRoute(to))} /> : undefined}
      edit={edit ? (props) => <Redirect to={route(getEditLink(to, props.match.params.id ?? ""))} /> : undefined}
      clone={clone ? (props) => <Redirect to={route(getCloneLink(to, props.match.params.id ?? ""))} /> : undefined}
      children={children}
    />
  );
};

// Legacy route via API mapping

interface RedirectModel {
  id: number;
  entityType: GenericEntityConstantsEntities;
}

interface LegacyRouteViaAPIProps extends Omit<LegacyRouteRedirectProps, "to" | "index" | "from"> {
  legacyRoute: string;
  apiEntityType: string;
  indexRoute: RouteKey;
}

/**
 * A component that handles redirection from legacy routes to new routes via API lookups.
 * @author @CorradoSurmanowicz
 * This component is used to map old/legacy route paths to new frontend routes, optionally
 * performing API lookups to resolve entity IDs and details before redirecting.
 *
 * @param legacyRoute - The legacy route key to redirect from.
 * @param apiEntityType - The API entity type used for lookups in detail, edit, and clone routes.
 * @param indexRoute - The new index route to redirect to.
 * @param detail - If true, enables redirection for detail routes using API lookup (default: true).
 * @param create - If true, enables redirection for create routes (default: true).
 * @param edit - If true, enables redirection for edit routes using API lookup (default: true).
 * @param clone - If true, enables redirection for clone routes using API lookup (default: true).
 * @param children - Optional child components to render.
 *
 * @remarks
 * - Uses `SessionContext` to resolve the current route function.
 * - Wraps redirection logic for index, detail, create, edit, and clone routes.
 * - For detail, edit, and clone, uses `LegacyRouteAPIWrapper` to fetch entity data before redirecting.
 */
export const LegacyRouteViaAPI = ({
  legacyRoute,
  apiEntityType,
  indexRoute,
  detail = true,
  create = false, // We set this to false by default to avoid confusion
  edit = true,
  clone = true,
  children,
}: LegacyRouteViaAPIProps) => {
  const { route } = useContext(SessionContext);

  return (
    <RouteRedirect
      from={legacyRoute as RouteKey}
      index={indexRoute ? (props) => <Redirect to={route(getIndexRoute(indexRoute))} /> : undefined}
      detail={
        detail
          ? (props) => {
              // console.log("Detail route");
              const id = props.match.params.id;
              if (!id) return undefined;
              if (isNaN(Number(id))) {
                return <Redirect to={route(getIndexRoute(indexRoute))} />;
              }
              return (
                <LegacyRouteAPIWrapper
                  id={id}
                  entityType={apiEntityType}
                  to={(result, frontendIndexRoute) => route(getDetailLink(frontendIndexRoute, result.id))}
                />
              );
            }
          : undefined
      }
      create={
        create
          ? (props) => {
              // console.log("Create route");
              return <Redirect to={route(getAddRoute(indexRoute))} />;
            }
          : undefined
      }
      edit={
        edit
          ? (props) => {
              // console.log("Edit route");
              const id = props.match.params.id;
              if (!id) return undefined;
              if (isNaN(Number(id))) {
                return <Redirect to={route(getIndexRoute(indexRoute))} />;
              }
              return (
                <LegacyRouteAPIWrapper
                  id={id}
                  entityType={apiEntityType}
                  to={(result, frontendIndexRoute) => route(getEditLink(frontendIndexRoute, result.id))}
                />
              );
            }
          : undefined
      }
      clone={
        clone
          ? (props) => {
              // console.log("Clone route");
              const id = props.match.params.id;
              if (!id) return undefined;
              if (isNaN(Number(id))) {
                return <Redirect to={route(getIndexRoute(indexRoute))} />;
              }
              <LegacyRouteAPIWrapper
                id={id}
                entityType={apiEntityType}
                to={(result, frontendIndexRoute) => route(getCloneLink(frontendIndexRoute, result.id))}
              />;
            }
          : undefined
      }
      children={children}
    />
  );
};

interface LegacyRouteAPIWrapperProps {
  id: number | string;
  to: (result: RedirectModel, frontendIndexRoute: RouteKey) => string;
  entityType: LegacyRouteViaAPIProps["apiEntityType"];
}

/**
 * A wrapper component that handles fetching redirect information for legacy routes,
 * displays a loading or error state as appropriate, and performs a redirect when data is available.
 * @author @CorradoSurmanowicz
 * @param id - The identifier for the legacy entity to be redirected.
 * @param entityType - The type of the entity for which the redirect is being performed.
 * @param to - A function that receives the redirect model and the frontend index route,
 *   and returns the target path for the redirect.
 *
 * @returns A React element that shows a loading state, error state, or performs a redirect based on the fetch result.
 */
const LegacyRouteAPIWrapper = ({ id, entityType, to }: LegacyRouteAPIWrapperProps) => {
  const {
    data: result,
    error,
    status,
    fetchStatus,
  } = useGet<RedirectModel, { entityType: string }>(`redirect/${id}` as ResourceName, {
    entityType: entityType,
  });

  return (
    <LoadingWrapper status={status} error={error} fetchStatus={fetchStatus}>
      {result && getEntityConstants(result.entityType) && (
        <Redirect to={to(result, getEntityConstants(result.entityType).entityConstants.frontendIndexRoute)} />
      )}
    </LoadingWrapper>
  );
};

// Core function

interface StaticContext {
  statusCode?: number | undefined;
}

type RenderProps = (
  props: RouteComponentProps<
    {
      [x: string]: string | undefined;
    },
    StaticContext,
    unknown
  >
) => React.ReactNode;

interface RouteRedirectProps {
  from: RouteKey;
  index?: RenderProps;
  detail?: RenderProps;
  create?: RenderProps;
  edit?: RenderProps;
  clone?: RenderProps;
  children?: React.ReactNode;
}

/**
 * A component that handles legacy route redirection logic using a set of provided route handlers.
 * @author @CorradoSurmanowicz
 * Renders a set of `<Route>` components based on the presence of handler props (`detail`, `create`, `edit`, `clone`, `index`).
 * Each handler is associated with a specific route, generated from the `from` prop using helper functions.
 * The component also renders any additional children passed to it.
 *
 * @param from - The base route or resource identifier used to generate specific routes.
 * @param index - Optional render function for the index (list) route.
 * @param detail - Optional render function for the detail (view) route.
 * @param create - Optional render function for the create (add) route.
 * @param edit - Optional render function for the edit route.
 * @param clone - Optional render function for the clone route.
 * @param children - Optional additional React nodes to render within the `<Switch>`.
 *
 * @returns A set of `<Route>` components wrapped in a `<Switch>`, handling legacy route redirection.
 */
const RouteRedirect = ({ from, index, detail, create, edit, clone, children }: RouteRedirectProps) => {
  const { route } = useContext(SessionContext);

  return (
    <Switch>
      {index && <Route path={route(getIndexRoute(from))} render={(props) => index(props)} exact />}
      {create && <Route path={route(getAddRoute(from))} render={(props) => create(props)} />}
      {edit && <Route path={route(getEditRoute(from))} render={(props) => edit(props)} />}
      {clone && <Route path={route(getCloneRoute(from))} render={(props) => clone(props)} />}
      {detail && <Route path={route(getDetailRoute(from))} render={(props) => detail(props)} />}
      {children}
    </Switch>
  );
};
